import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApiError,
  Booking,
  Discount,
  EventLocation,
  GameMaster,
  IEvent,
  MyBooking,
  PrepaymentInfo,
} from '../api/events.types';
import {
  fetchEvents,
  fetchEventDetails,
  buyTicketsByGold,
  getEventPaymentUrl,
  checkEventPaymentStatus,
} from './events.thunk';

const calculateCurrentPrices = (state: EventsState) => {
  if (!state.details.data) return;

  const baseFiat = state.details.data.fiatCost;
  const baseGold = state.details.data.goldCost;

  const totalTickets = state.ticketCount + 1;
  state.totalFiat = totalTickets * (state.discount?.fiatEventPrice ?? baseFiat);
  state.totalGold = totalTickets * (state.discount?.goldEventPrice ?? baseGold);
};

const calculatePrepaymentAmounts = (state: EventsState) => {
  if (!state.prepayment) return;

  const totalTickets = state.ticketCount + 1;

  if (state.prepayment.mode === 'required' || state.prepayment.mode === 'full_only') {
    state.prepaymentAmount = totalTickets * (state.prepayment.prepaymentAmount || 0);
    state.fullPaymentAmount = totalTickets * state.prepayment.fullAmount;
    state.remainingAmount = totalTickets * (state.prepayment.remainingAmount || 0);

  }
};

interface EventsState {
  list: {
    items: IEvent[];
    loading: boolean;
    error: ApiError | null;
  };
  details: {
    data: IEvent | null;
    bookings: Booking[];
    myBooking: MyBooking | null;
    location: EventLocation | null;
    gameMasters: GameMaster[];
    loading: boolean;
    error: ApiError | null;
  };
  paymentUrl: {
    url: string | null;
    id: string | null;
    loading: boolean;
    error: ApiError | null;
  };
  discount: Discount | null;

  ticketCount: number;
  totalFiat: number;
  totalGold: number;
  purchaseLoading: boolean;
  purchaseError: ApiError | null;
  prepayment: PrepaymentInfo | null;
  prepaymentAmount: number;
  remainingAmount: number;
  fullPaymentAmount: number;
  isFullPayment: boolean;
}

const initialState: EventsState = {
  list: {
    items: [],
    loading: false,
    error: null,
  },
  details: {
    data: null,
    bookings: [],
    myBooking: null,
    location: null,
    gameMasters: [],
    loading: false,
    error: null,
  },
  paymentUrl: {
    url: null,
    id: null,
    loading: false,
    error: null,
  },
  discount: null,

  ticketCount: 0,
  totalFiat: 0,
  totalGold: 0,
  purchaseLoading: false,
  purchaseError: null,
  prepayment: null,
  prepaymentAmount: 0,
  remainingAmount: 0,
  fullPaymentAmount: 0,
  isFullPayment: false,
};

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    incrementTicketCount: (state) => {
      if (state.ticketCount < 24) {
        state.ticketCount += 1;
        calculateCurrentPrices(state);
        calculatePrepaymentAmounts(state);
      }
    },
    decrementTicketCount: (state) => {
      if (state.ticketCount > 0) {
        state.ticketCount -= 1;
        calculateCurrentPrices(state);
        calculatePrepaymentAmounts(state);
      }
    },
    resetTicketCount: (state) => {
      state.ticketCount = 0;
      calculateCurrentPrices(state);
    },
    setTicketCount: (state, action: PayloadAction<number>) => {
      state.ticketCount = action.payload;
      calculateCurrentPrices(state);
    },
    toggleFullPayment: (state) => {
      state.isFullPayment = !state.isFullPayment;
    },
    setFullPayment: (state, action: PayloadAction<boolean>) => {
      state.isFullPayment = action.payload;
    },
    clearEventDetails: (state) => {
      state.details = initialState.details;
    },
    clearPurchaseError: (state) => {
      state.purchaseError = null;
    },
    clearPaymentUrl: (state) => {
      state.paymentUrl = initialState.paymentUrl;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.items = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload || {
          code: 500,
          message: 'Unknown error',
        };
      });

    builder
      .addCase(fetchEventDetails.pending, (state) => {
        state.details.loading = true;
        state.details.error = null;
      })
      .addCase(fetchEventDetails.fulfilled, (state, action) => {
        state.details.loading = false;
        state.details.data = action.payload.event;
        state.details.bookings = action.payload.bookings;
        state.details.myBooking = action.payload.myBooking;
        state.details.location = action.payload.location;
        state.details.gameMasters = action.payload.gameMasters;
        state.discount = action.payload.discount;
        state.prepayment = action.payload.prepayment;

        if (action.payload.myBooking) {
          const bookingQuantity = action.payload.myBooking.quantity;
          const friendsCount = Math.max(0, bookingQuantity - 1);
          state.ticketCount = friendsCount;
        }

        const totalTickets = state.ticketCount + 1;
        const prepaymentInfo = action.payload.prepayment;

        if (prepaymentInfo) {
          if (prepaymentInfo.mode === 'required' || prepaymentInfo.mode === 'full_only') {
            state.prepaymentAmount = totalTickets * (prepaymentInfo.prepaymentAmount || 0);
            state.fullPaymentAmount = totalTickets * prepaymentInfo.fullAmount;
            state.remainingAmount = totalTickets * (prepaymentInfo.remainingAmount || 0);
          }
        }

        const currentFiat =
          action.payload.discount?.fiatEventPrice ?? action.payload.event.fiatCost;
        const currentGold =
          action.payload.discount?.goldEventPrice ?? action.payload.event.goldCost;
        state.totalFiat = totalTickets * currentFiat;
        state.totalGold = totalTickets * currentGold;
      })

      .addCase(fetchEventDetails.rejected, (state, action) => {
        state.details.loading = false;
        state.details.error = action.payload || {
          code: 500,
          message: 'Unknown error',
        };
      })
      .addCase(buyTicketsByGold.pending, (state) => {
        state.purchaseLoading = true;
        state.purchaseError = null;
      })
      .addCase(buyTicketsByGold.fulfilled, (state) => {
        state.purchaseLoading = false;
      })
      .addCase(buyTicketsByGold.rejected, (state, action) => {
        state.purchaseLoading = false;
        state.purchaseError = action.payload || {
          code: 500,
          message: 'Unknown error',
        };
      })
      .addCase(getEventPaymentUrl.pending, (state) => {
        state.paymentUrl.loading = true;
        state.paymentUrl.error = null;
      })
      .addCase(getEventPaymentUrl.fulfilled, (state, action) => {
        state.paymentUrl.loading = false;
        state.paymentUrl.url = action.payload.url;
        state.paymentUrl.id = action.payload.id;
      })
      .addCase(getEventPaymentUrl.rejected, (state, action) => {
        state.paymentUrl.loading = false;
        state.paymentUrl.error = action.payload || {
          code: 500,
          message: 'Unknown error',
        };
      })
      .addCase(checkEventPaymentStatus.pending, (state) => {
        state.purchaseLoading = true;
        state.purchaseError = null;
      })
      .addCase(checkEventPaymentStatus.fulfilled, (state) => {
        state.purchaseLoading = false;
      })
      .addCase(checkEventPaymentStatus.rejected, (state, action) => {
        state.purchaseLoading = false;
        state.purchaseError = action.payload || {
          code: 500,
          message: 'Unknown error',
        };
      });
  },
});

export const {
  clearEventDetails,
  incrementTicketCount,
  decrementTicketCount,
  resetTicketCount,
  setTicketCount,
  clearPurchaseError,
  toggleFullPayment,
  setFullPayment,
} = eventsSlice.actions;
export default eventsSlice.reducer;
